import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API, errorText, formatINR, paymentStateColors } from "@/lib/payments";

const ConfigBanner = ({ config }) => config && (
  <div data-testid="payments-config-banner"
    className={`rounded-xl p-4 flex gap-3 text-sm ${config.payments_enabled ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-900"}`}>
    {config.payments_enabled ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <AlertTriangle className="h-5 w-5 shrink-0" />}
    <div>
      <p className="font-medium">{config.payments_enabled ? `PhonePe payments enabled (${config.environment})` : "PhonePe payments are disabled"}</p>
      {!config.payments_enabled && <p className="mt-1">Missing backend environment variables: <span className="font-mono">{config.missing.join(", ")}</span></p>}
    </div>
  </div>
);

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

export const PaymentsTab = ({ authHeader }) => {
  const [data, setData] = useState({ payments: [], refunds: [] });
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/admin/payments`, { headers: authHeader() }),
        axios.get(`${API}/admin/payments/config`, { headers: authHeader() }),
      ]);
      setData(p.data);
      setConfig(c.data);
    } catch (err) {
      toast.error(errorText(err, "Could not load payments"));
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const refresh = async (id) => {
    setBusy(id);
    try {
      const res = await axios.post(`${API}/admin/payments/${id}/refresh`, {}, { headers: authHeader() });
      toast.success(`Status: ${res.data.phonepe_state}`);
      await load();
    } catch (err) {
      toast.error(errorText(err, "Status check failed"));
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="payments-tab">
      <ConfigBanner config={config} />
      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Payments ({data.payments.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {data.payments.length === 0 ? <p className="text-stone-500 text-sm" data-testid="payments-empty">No payment attempts yet</p> : (
            <Table data-testid="payments-table">
              <TableHeader><TableRow>
                <TableHead>Quotation</TableHead><TableHead>Customer</TableHead><TableHead>Amount</TableHead>
                <TableHead>Status</TableHead><TableHead>PhonePe order</TableHead><TableHead>Created</TableHead><TableHead>Updated</TableHead><TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {data.payments.map((p) => (
                  <TableRow key={p.merchant_order_id} data-testid={`payment-row-${p.merchant_order_id}`}>
                    <TableCell className="font-mono text-xs">{p.quotation_number || "—"}<br /><span className="text-stone-400">{p.merchant_order_id}</span></TableCell>
                    <TableCell>{p.customer_name || "—"}{p.company_name && <span className="block text-xs text-stone-500">{p.company_name}</span>}</TableCell>
                    <TableCell>{formatINR(p.amount_paise ?? p.expected_amount_paise)}</TableCell>
                    <TableCell><Badge className={paymentStateColors[p.phonepe_state] || ""}>{p.phonepe_state}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{p.phonepe_order_id || "—"}</TableCell>
                    <TableCell className="text-xs">{fmt(p.created_at)}</TableCell>
                    <TableCell className="text-xs">{fmt(p.updated_at)}</TableCell>
                    <TableCell>
                      {p.phonepe_state === "PENDING" && config?.payments_enabled && (
                        <Button size="sm" variant="ghost" disabled={busy === p.merchant_order_id} onClick={() => refresh(p.merchant_order_id)} data-testid={`payment-refresh-btn-${p.merchant_order_id}`}>
                          <RefreshCw className={`h-4 w-4 ${busy === p.merchant_order_id ? "animate-spin" : ""}`} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Refunds ({data.refunds.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {data.refunds.length === 0 ? <p className="text-stone-500 text-sm" data-testid="refunds-empty">No refunds received</p> : (
            <Table data-testid="refunds-table">
              <TableHeader><TableRow><TableHead>Refund ID</TableHead><TableHead>Original order</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Updated</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.refunds.map((r) => (
                  <TableRow key={r.refund_id} data-testid={`refund-row-${r.refund_id}`}>
                    <TableCell className="font-mono text-xs">{r.refund_id}</TableCell>
                    <TableCell className="font-mono text-xs">{r.original_merchant_order_id || "—"}</TableCell>
                    <TableCell>{r.amount_paise != null ? formatINR(r.amount_paise) : "—"}</TableCell>
                    <TableCell><Badge className={paymentStateColors[r.phonepe_state] || ""}>{r.phonepe_state}</Badge></TableCell>
                    <TableCell className="text-xs">{fmt(r.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
